import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Input,
  Label,
  Button,
  Spinner,
} from "reactstrap";
import { useMutation } from "@tanstack/react-query";
import { EditSchoolName } from "../../actions/superUserMutations";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { moveUserToClasses } from "../../Utilities/moveUserHelper";
import { compareByName } from "../../Utilities/sortHelper";

const getSchoolClasses = (school) =>
  (school?.programmes ?? [])
    .flatMap((programme) => programme.classes ?? [])
    .sort(compareByName);

export default function SuperuserSchoolEditModal(props) {
  const intl = useIntl();
  const [newName, setNewName] = useState(props.schoolToUpdate?.name);
  const [teacherToMoveEmail, setTeacherToMoveEmail] = useState("");
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [isMovingTeacher, setIsMovingTeacher] = useState(false);
  const postEditSchoolName = useMutation({ mutationFn: EditSchoolName });
  const queryClient = useQueryClient();

  const schoolClasses = getSchoolClasses(props.schoolToUpdate);
  const allClassesSelected = selectedClassIds.length === schoolClasses.length;
  const canMoveTeacher =
    teacherToMoveEmail.trim() !== "" &&
    selectedClassIds.length > 0 &&
    !isMovingTeacher;

  useEffect(() => {
    setNewName(props.schoolToUpdate?.name);
  }, [props.schoolToUpdate?.name]);

  useEffect(() => {
    setSelectedClassIds(
      getSchoolClasses(props.schoolToUpdate).map(
        (schoolClass) => schoolClass.id,
      ),
    );
  }, [props.schoolToUpdate]);

  const toggleClass = (classId) =>
    setSelectedClassIds((current) =>
      current.includes(classId)
        ? current.filter((selectedId) => selectedId !== classId)
        : [...current, classId],
    );

  const toggleAllClasses = () =>
    setSelectedClassIds(
      allClassesSelected
        ? []
        : schoolClasses.map((schoolClass) => schoolClass.id),
    );

  const handleToggle = () => {
    setNewName("");
    setTeacherToMoveEmail("");
    props.toggle();
  };

  const handleEditSchoolName = () => {
    if (newName.length > 0 && newName !== props.schoolToUpdate?.name) {
      postEditSchoolName.mutate(
        { id: props.schoolToUpdate.id, name: newName },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["FetchSchoolsForAdmin"] });
            toast.success(<FormattedMessage id="NameChanged" />);
            handleToggle();
          },
          onError: (err) => {
            toast.warn(formatServiceError(intl, err?.response?.data));
            handleToggle();
          },
        }
      );
    }
  };

  const handleMoveTeacher = async () => {
    setIsMovingTeacher(true);

    const moved = await moveUserToClasses(
      intl,
      teacherToMoveEmail.trim(),
      selectedClassIds,
      true,
    );

    setIsMovingTeacher(false);

    if (moved) {
      handleToggle();
    }
  };

  return (
    <Modal isOpen={props.isOpen} toggle={handleToggle}>
      <ModalHeader>
        {props.schoolToUpdate && `Uppdatera ${props.schoolToUpdate.name}`}
      </ModalHeader>
      <ModalBody>
        <fieldset>
          <legend className="fs-6 fw-semibold mb-2">
            <FormattedMessage id="ChangeNameOfSchool" />
          </legend>
          <Input
            type="text"
            onChange={(event) => {
              setNewName(event.target.value);
            }}
            defaultValue={props.schoolToUpdate?.name}
          />
          <Button
            color="primary"
            outline
            className="mt-2"
            onClick={() => handleEditSchoolName()}
          >
            <FormattedMessage id="Save" />
          </Button>
        </fieldset>
      </ModalBody>
      <ModalBody className="border-top">
        <fieldset>
          <legend className="fs-6 fw-semibold mb-2">
            <FormattedMessage id="MoveTeacherToThisSchool" />
          </legend>
          <Input
            type="email"
            name="email"
            id="email"
            placeholder={intl.formatMessage({ id: "TeacherEmail" })}
            onChange={(event) => setTeacherToMoveEmail(event.target.value)}
          />
          <div className="d-flex align-items-center justify-content-between pt-2">
            <Label className="mb-0">
              <FormattedMessage id="ClassesTheTeacherGets" />
            </Label>
            <Button
              color="link"
              size="sm"
              className="p-0"
              disabled={schoolClasses.length === 0}
              onClick={toggleAllClasses}
            >
              <FormattedMessage
                id={allClassesSelected ? "DeselectAll" : "SelectAll"}
              />
            </Button>
          </div>
          <div
            className="border rounded px-2 py-1"
            style={{ maxHeight: "12rem", overflowY: "auto" }}
          >
            {schoolClasses.map((schoolClass) => (
              <FormGroup check key={schoolClass.id} className="mb-0">
                <Input
                  type="checkbox"
                  id={`move-teacher-class-${schoolClass.id}`}
                  checked={selectedClassIds.includes(schoolClass.id)}
                  onChange={() => toggleClass(schoolClass.id)}
                />
                <Label check for={`move-teacher-class-${schoolClass.id}`}>
                  {schoolClass.name}
                </Label>
              </FormGroup>
            ))}
          </div>
          <Button
            color="primary"
            outline
            className="mt-2"
            disabled={!canMoveTeacher}
            onClick={handleMoveTeacher}
          >
            {isMovingTeacher ? (
              <>
                <Spinner size="sm" className="me-2" />
                <FormattedMessage id="Moving" />
              </>
            ) : (
              <FormattedMessage id="Move" />
            )}
          </Button>
        </fieldset>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={() => handleToggle()}>
          <FormattedMessage id="Cancel" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
